import { Interface } from '@ethersproject/abi';
import { adaptEthersSigner } from '@reservoir0x/relay-ethers-wallet-adapter';
import { AdaptedWallet, getClient, GetQuoteParameters, ProgressData } from '@reservoir0x/relay-sdk';
import { claims } from '@manifoldxyz/claim-contracts';
import { NULL_ADDRESS } from '@manifoldxyz/js-ts-utils/constants';
import { ClaimStudioInstance, ScribeStudioInstance } from '@/api/models/studioInstance';
import networkCheck from '@/common/networkCheck';
import { getExtensionAddress } from '@/common/util';
import ClaimExtensionContract from '@/contracts/claimExtensionContract';

/**
 * Executes a relay transaction
 * @param originNetwork - The network id of the origin network
 * @param destinationNetwork - The network id of the destination network
 * @param callConfig - The relay call config required to get quote and execute the mint
 * @param onProgress - The function to call when the progress changes
 * @returns The transaction hash of the transaction
 */
export async function executeRelayTransaction(
  originNetwork: number,
  destinationNetwork: number,
  parameters: GetQuoteParameters,
  onProgress?: (progress: ProgressData) => void,
): Promise<string> {
  if (!parameters.wallet) {
    throw new Error('No wallet found, unable to execute relay transaction');
  }
  // Check and ask user to switch to the correct network
  await networkCheck(originNetwork);

  const quote = await getClient().actions.getQuote(parameters);
  return new Promise<string>((resolve, reject) =>
    getClient()
      .actions.execute({
        quote,

        wallet: parameters.wallet as AdaptedWallet,
        onProgress: async (progress: ProgressData) => {
          onProgress?.(progress);
          // Transaction is completed when the transaction on the destination network is confirmed
          const tx = progress.txHashes?.find(
            (transaction) => transaction.chainId === destinationNetwork,
          )?.txHash;
          if (progress.refunded === true) {
            reject(new Error('Failed to execute relay mint, your funds have been refunded'));
          }
          if (!progress.currentStep && progress.refunded === undefined && tx) {
            resolve(tx);
          }
        },
      })
      .catch((error) => {
        console.error('Error executing relay transaction', error);
        reject(error);
      }),
  );
}

/**
 * Gets the parameters for a relay mint. This will be used to get a quote and execute the mint.
 * @param studioInstance - The studio instance
 * @param recipient - The recipient of the mint
 * @param numberOfMints - The number of mints to execute
 * @returns The call config for a relay mint
 */
export async function getMintRelayParameters({
  chainId,
  studioInstance,
  recipient,
  numberOfMints,
  withSigner = false,
}: {
  chainId: number;
  studioInstance: ClaimStudioInstance | ScribeStudioInstance;
  recipient: string;
  numberOfMints: number;
  withSigner?: boolean;
}): Promise<GetQuoteParameters> {
  const enabled = enableRelayMint(studioInstance);
  if (!enabled) {
    throw new Error('Relay mint is not enabled for this claim');
  }
  let wallet: AdaptedWallet | undefined;

  if (withSigner) {
    const signer = window.ManifoldEthereumProvider.provider(true)?.getSigner();
    if (!signer) throw new Error('No signer found, unable to fetch quote');
    wallet = adaptEthersSigner(signer);
  }

  const creatorContractAddress = studioInstance.publicData.contract.contractAddress;
  const extensionContractAddress = getExtensionAddress(studioInstance);
  const claimIndex = parseInt(
    (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() || studioInstance.id,
  );

  // Instantiate contract for fee logic
  const claimContract = new ClaimExtensionContract(
    studioInstance.publicData.network,
    extensionContractAddress,
    creatorContractAddress,
    claimIndex,
  );

  const value = await claimContract.getMintValue(
    numberOfMints,
    studioInstance.publicData.mintPrice,
    false,
  );

  const valueString = value.toString();

  const toChainId = studioInstance?.publicData.network;
  const functionName = 'mintProxy';
  const mintSig =
    'function mintProxy(address creatorContractAddress, uint256 instanceId, uint16 mintCount, uint32[] mintIndices, bytes32[][] merkleProofs, address mintFor)';
  const args = [creatorContractAddress, claimIndex, numberOfMints, [], [], recipient];

  // Relay mint only support native currency for now
  const currency = '0x0000000000000000000000000000000000000000';
  const txInterface = new Interface([mintSig]);
  const callData = txInterface.encodeFunctionData(functionName, args);

  return {
    chainId,
    toChainId,
    currency,
    toCurrency: currency,
    tradeType: 'EXACT_OUTPUT',
    amount: valueString,
    recipient,
    wallet,
    txs: [
      {
        to: extensionContractAddress,
        value: valueString,
        data: callData,
      },
    ],
  };
}

/**
 * We can enable relay minting if all of the following are true:
 * - The claim is not an allowlist claim
 * - The claim is not a signature claim
 * - The claim does not use erc20 as payment
 * - The extension contract support `mintProxy`
 */
export function enableRelayMint(studioInstance: ClaimStudioInstance | ScribeStudioInstance) {
  const ext = claims.getExtension(getExtensionAddress(studioInstance)) as claims.ClaimDeployment;
  if (!claims.hasTrait(ext, claims.Trait.Proxy)) {
    return false;
  }

  if (studioInstance.publicData.mintPrice.erc20 !== NULL_ADDRESS) {
    return false;
  }

  return studioInstance.publicData.mintingRestriction === 'None';
}
